"use client";

import { notifications } from "@mantine/notifications";
import { CategoryActions } from "Components/Admin/AdminProduct/AdminCategory/CategoryActions";
import { CategoryForm } from "Components/Admin/AdminProduct/AdminCategory/CategoryForm";
import { CategoryTable } from "Components/Admin/AdminProduct/AdminCategory/CategoryTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEffect, useState } from "react";
import {
  useAddCategoryMutation,
  useBulkDeleteCategoriesMutation,
  useDeleteCategoryImageMutation,
  useDeleteCategoryMutation,
  useGetAllCategoriesQuery,
  useUpdateCategoryMutation,
  useUploadCategoryImageMutation,
} from "Services/Admin/CategoryAdminApiSlice";
import {
  CategoryAdminDTO,
  CategoryAdminRequestDTO,
} from "Types/Admin/AdminCategoryType";

const PLANT = process.env.REACT_APP_PLANT;

const AdminCategory = () => {
  const {
    data: apiCategories,
    isLoading,
    refetch,
  } = useGetAllCategoriesQuery();
  const categories: CategoryAdminDTO[] = apiCategories || [];

  const [createCategory] = useAddCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [uploadCategoryImage] = useUploadCategoryImageMutation();
  const [deleteCategoryImage] = useDeleteCategoryImageMutation();

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<CategoryAdminDTO | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );

  const [bulkDeleteCategories] = useBulkDeleteCategoriesMutation();
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  const [filteredCategories, setFilteredCategories] = useState<
    CategoryAdminDTO[]
  >([]);
  const [searchText, setSearchText] = useState("");

  const categoryMappingFromMstToRequest = (
    categoryMst: CategoryAdminDTO | null,
  ) => {
    if (categoryMst != null) {
      return {
        categoryCode: categoryMst.categoryCode,
        categoryName: categoryMst.categoryName,
        isActive: categoryMst.isActive,
      } as CategoryAdminRequestDTO;
    } else {
      return null;
    }
  };

  useEffect(() => {
    let filtered = categories ? [...categories] : [];

    if (searchText) {
      filtered = filtered.filter((category) =>
        (category.categoryName || "")
          .toLowerCase()
          .includes(searchText.toLowerCase()),
      );
    }

    const currentFilteredJSON = JSON.stringify(filtered);
    const previousFilteredJSON = JSON.stringify(filteredCategories);

    if (currentFilteredJSON !== previousFilteredJSON) {
      setFilteredCategories(filtered);
    }
  }, [categories, searchText]);

  const handleViewCategory = async (id: number) => {
    try {
      const categoryToView = categories.find((category) => category.id === id);
      if (categoryToView) {
        setEditingCategory(categoryToView);
        setViewMode(true);
        setShowForm(true);
      }
      refetch();
    } catch (error) {
      console.error("Failed to get category details:", error);
    }
  };

  const handleEditCategory = async (id: number) => {
    try {
      const categoryToEdit = categories.find((category) => category.id === id);
      if (categoryToEdit) {
        setEditingCategory(categoryToEdit);
        setViewMode(false);
        setShowForm(true);
      }
      refetch();
    } catch (error) {
      console.error("Failed to get category details:", error);
    }
  };

  const handleSubmit = async (
  data: CategoryAdminRequestDTO,
  imageFile?: File,
) => {
  try {
    if (editingCategory) {
      const updatedData = {
        categoryCode: data.categoryCode,
        categoryName: data.categoryName,
        isActive: data.isActive,
      };

      const updateRes = await updateCategory({
        id: editingCategory.id,
        data: updatedData,
      }).unwrap();

      if (imageFile) {
        await uploadCategoryImage({
          id: editingCategory.id,
          image: imageFile,
        }).unwrap();
      }

      notifications.show({
        message: updateRes.message,
        color: "teal",
      });
      refetch();
    } else {
      const response = await createCategory({ data }).unwrap();
      const id = response.results;

      if (imageFile) {
        await uploadCategoryImage({
          id,
          image: imageFile,
        }).unwrap();
      }
      await refetch();
      notifications.show({
        message: response.message,
        color: "teal",
      });
    }

    setShowForm(false);
    setEditingCategory(null);
    refetch();
  } catch (error: any) {
    let errorMessage = "We couldn't save the category. Please try again.";
    
    if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    notifications.show({
      message: errorMessage,
      color: "red",
    });

    console.error("Failed to save category:", error);
  }
};

  const handleDeleteCategory = async (id: number) => {
  try {
    const res = await deleteCategory({ id }).unwrap();
    notifications.show({
      message: res.message,
      color: "teal",
    });
    refetch();
  } catch (error: any) {
    let errorMessage = "Failed to delete category";
    
    if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    notifications.show({
      message: errorMessage,
      color: "red",
    });
    console.error("Failed to delete category:", error);
  }
};

  const handleDeleteCategoryImage = async () => {
  if (!editingCategory) return;

  try {
    const res = await deleteCategoryImage({ id: editingCategory.id }).unwrap();

    notifications.show({
      message: res.message,
      color: "teal",
    });

    // Update local state so the UI reflects the removed image
    setEditingCategory((prev) => (prev ? { ...prev, image: "" } : prev));
    refetch();
  } catch (error: any) {
    let errorMessage = "Failed to delete category image";
    
    if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    notifications.show({
      message: errorMessage,
      color: "red",
    });
    console.error("Failed to delete category image:", error);
  }
};

  const handleTableSelect = (ids: number[]) => {
    setSelectedCategories(ids);
    setSelectedCategoryId(ids.length > 0 ? ids[0] : null);
  };

  const handleBulkDelete = async () => {
  if (selectedCategories.length === 0) return;

  try {
    const count = selectedCategories.length;
    const res = await bulkDeleteCategories({ ids: selectedCategories }).unwrap();

    notifications.show({
      message: res.message,
      color: "teal",
    });

    setSelectedCategories([]);
    setSelectedCategoryId(null);
    refetch();
  } catch (error: any) {
    let errorMessage = "Failed to delete categories";
    
    if (error.data?.message) {
      errorMessage = error.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    notifications.show({
      message: errorMessage,
      color: "red",
    });
    console.error("Failed to bulk delete categories:", error);
  }
};

if (isLoading && !showForm) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Categories"
            onAdd={() => setShowForm(true)}
            addLabel="Add Category"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <CategoryActions
              onSearchChange={setSearchText}
              isDeleteDisabled={selectedCategories.length === 0}
              onBulkDelete={handleBulkDelete}
            />
            <CategoryTable
              categories={filteredCategories}
              onSelectCategory={handleTableSelect}
              onDeleteCategory={handleDeleteCategory}
              onViewCategory={handleViewCategory}
              onEditCategory={handleEditCategory}
              selectedCategory={selectedCategoryId}
              isLoading={isLoading}
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingCategory(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Category"
                : editingCategory
                  ? "Edit Category"
                  : "Add Category"
            }
          />
          <CategoryForm
            initialValues={
              categoryMappingFromMstToRequest(editingCategory) || {
                categoryCode: "",
                categoryName: "",
                isActive: "Y",
              }
            }
            existingImage={editingCategory?.image ?? undefined}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingCategory(null);
            }}
            onDeleteImage={handleDeleteCategoryImage}
            mode={viewMode ? "view" : editingCategory ? "edit" : "add"}
          />
        </>
      )}
    </div>
  );
};

export default AdminCategory;
